import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CancelIcon from "@mui/icons-material/Clear";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployee } from "../../api/employee";
import { fetchEmployeeSchedulesByDateRange } from "../../api/scheduleEditor";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs";
import StarsSelectInput from "../../components/fundamentals/StarsSelectInput";
import PageLoading from "../../components/PageLoading";
import CustomBaseScheduleDialog from "../../components/schedule/CustomBaseScheduleDialog";
import EmployeeDailySchedule from "../../components/schedule/EmployeeDailySchedule";
import ScheduleEditorCalendar from "../../components/schedule/ScheduleEditorCalendar";
import UnauthorizedMessage from "../../components/UnauthorizedMessage";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../../slices/userSession/userSessionSlice";
import { selectWebSocketEventData } from "../../slices/webSocketEvent/webSocketEventSlice";
import { logout } from "../../util/authentication";
import { getDayStateForScheduleEditor } from "../../util/calendar";
import { getAdminStation, getMultiStationAdminStationList, sortOnCaseSensitiveTexts } from "../../util/commonUtils";
import {
  BREADCRUMBS_TITLE,
  EMPLOYEE_ID,
  FEATURES,
  HOME_PATH,
  MULTI_STATION_ADMIN_ACCESS,
  STATION,
} from "../../util/constants";
import { getUserDataFromEmployeeService } from "../../util/employeeDataRetriever";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  INVALID_EMPLOYEE_ID,
  NO_ACCESS_TO_PAGE_MESSAGE,
  STATION_FILTER_MUST_BE_SELECTED,
} from "../../util/messageConstants";
import { validateEmployeeDataForAdminSchedule, validateScheduleEmployeeId } from "../../util/schedule/inputValidation";
import {
  buildEmployeeDataFromBidRedirect,
  getCurrentMonth,
  getCurrentYear,
  getSelectableMonthsList,
  getSelectableYearsList,
} from "../../util/schedule/scheduleEditorUtils";
import { mergeWebSocketSchedules } from "../../util/webSocketUtils";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import { useLocation } from "react-router-dom";
import { fetchLatestBidEventByReferenceNumber, fetchLatestBidGroupEmpEventsByReferenceNumber } from "../../api/bid";

const useStyles = makeStyles((theme) => ({
  pageTitleLeft: {
    marginLeft: "2rem",
    fontSize: "24px",
    maxWidth: "90vw",
    textAlign: "left",
    marginTop: "10px",
  },
  inputText: {
    width: "12vw",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  inputSelect: {
    maxWidth: "17vw",
    textAlign: "left",
    width: "90px",
  },
  employeeDataTitle: {
    marginTop: "7px",
    color: theme.palette.secondary.swaGray,
  },
  employeeDataValue: {
    fontSize: "16px",
    marginTop: "-5px",
  },
  employeeDetails: {
    backgroundColor: theme.palette.secondary.swaGray1,
    height: "auto",
    borderRadius: "5px",
    marginLeft: "40px",
  },
  employeeName: {
    fontSize: "24px",
    paddingTop: "11px",
    paddingLeft: "16px",
    textAlign: "left",
  },
  employeeData: {
    textAlign: "left",
    maxHeight: "56px",
  },
  scheduleInputRow: {
    display: "flex",
    marginLeft: "-15px",
  },
  scheduleInputDate: {
    display: "flex",
    direction: "row",
    justifyContent: "flex-end",
  },
  scheduleInputMonth: {
    maxWidth: "17vw",
    textAlign: "left",
    width: "140px",
    marginRight: "10px",
  },
  scheduleInputEmployeeID: {
    paddingLeft: "10px",
  },
  scheduleCalendarHeader: {
    fontSize: "20px",
    fontWeight: "bold",
  },
}));

const ScheduleManagementPage = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const dispatch = useDispatch();
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [isEmployeeScheduleLoading, setIsEmployeeScheduleLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [station, setStation] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [selectableMonths, setSelectableMonths] = useState([]);
  const [selectableYears, setSelectableYears] = useState([]);
  const [employeeSchedules, setEmployeeSchedules] = useState([]);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");
  const [employeeIdSearchLoaded, setEmployeeIdSearchLoaded] = useState(false);
  const [employeeIdSearchTriggered, setEmployeeIdSearchTriggered] = useState(false);
  const scheduleWebSocketDataRef = useRef({});

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.SCHEDULE_MANAGEMENT];
  const breadcrumbsLink = [HOME_PATH];

  //Redirect from Bid Page state
  const location = useLocation();
  const [bidData, setBidData] = useState(null);
  const [hasAccessToViewPage, setHasAccessToViewPage] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  //Base Schedule Dialog state
  const [isBaseScheduleFromBidRedirect, setIsBaseScheduleFromBidRedirect] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      setStation(getAdminStation(props.featureConfig.userStations, userSession, stationFilter));
      setMonth(getCurrentMonth(station));
      setYear(getCurrentYear(station));
      setSelectableYears(getSelectableYearsList(station));
      setSelectableMonths(getSelectableMonthsList(getCurrentYear(station), station));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    //Automatically open Create Base Schedule dialog if coming from Bid Page
    if (isFirstLoad && pathnameHasBidData(location.pathname)) {
      fetchBidData(location.pathname.split("/")[2]);
    }

    setIsFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstLoad]);

  useEffect(() => {
    //Clear path after first load and after create/close schedule so it doesn't re-open with bid data
    if (!isFirstLoad && !isDialogOpen && pathnameHasBidData(location.pathname)) {
      props.history.push(`/${FEATURES.SCHEDULE_MANAGEMENT}`);
      setIsBaseScheduleFromBidRedirect(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen]);

  useEffect(() => {
    if (employeeData) {
      fetchEmployeeSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, employeeData]);

  useEffect(() => {
    if (Object.values(webSocketEventData.scheduleAdminEvents).length > 0) {
      scheduleWebSocketDataRef.current = webSocketEventData.scheduleAdminEvents;
      if (!isFirstLoad && employeeData) {
        setEmployeeSchedules((prevState) => {
          return mergeWebSocketSchedules(webSocketEventData.scheduleAdminEvents, station, employeeData.eid, prevState);
        });
        scheduleWebSocketDataRef.current = {};
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.scheduleAdminEvents]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  //#region API Calls
  const fetchEmployeeData = (eid) => {
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(true);
    setEmployeeIdErrors("");
    setEmployeeData(null);
    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let filteredEmployeeData = await getUserDataFromEmployeeService(result);
          let employeeDataErrors = validateEmployeeDataForAdminSchedule(filteredEmployeeData, station);
          if (employeeDataErrors === "") {
            setEmployeeData(filteredEmployeeData);
            setEmployeeIdSearchLoaded(true);
          } else {
            setEmployeeIdErrors(employeeDataErrors);
            setEmployeeIdSearchLoaded(true);
          }
        } else {
          setEmployeeIdErrors(INVALID_EMPLOYEE_ID);
          setEmployeeIdSearchLoaded(true);
        }
      });
  };

  const fetchEmployeeSchedule = () => {
    setIsEmployeeScheduleLoading(true);
    setEmployeeSchedules([]);
    const dates = getDayStateForScheduleEditor(year, month, []);
    const startDate = dates[0].additionalDate ? dates[0].additionalDate : dates[0].date;
    const endDate = dates[dates.length - 1].additionalDate
      ? dates[dates.length - 1].additionalDate
      : dates[dates.length - 1].date;

    let stationToQuery;
    if (employeeData.tempStation === station) {
      stationToQuery = employeeData.tempStation;
    }

    // We only need to query by station when employee is temping and querying from temp station
    // Otherwise we query for all schedules to show Temp chip at selected station (if temps available for that employee)
    fetchEmployeeSchedulesByDateRange(employeeData.eid, startDate, endDate, stationToQuery)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          setEmployeeSchedules(
            mergeWebSocketSchedules(scheduleWebSocketDataRef.current, station, employeeData.eid, result.event)
          );
          scheduleWebSocketDataRef.current = {};
          setIsEmployeeScheduleLoading(false);
          setIsFirstLoad(false);
        }
      })
      .catch((error) => {
        console.error("Error in fetchEmployeeSchedulesByDateRange: ", error);
        setIsEmployeeScheduleLoading(false);
      });
  };

  const fetchBidData = async (bidReferenceNumber) => {
    try {
      setIsPageLoading(true);
      const bidEventResponse = await fetchLatestBidEventByReferenceNumber(userSession.eid, bidReferenceNumber);
      const bidEventResult = await bidEventResponse.json();
      const bidEvent = bidEventResult.event;
      if (bidEvent) {
        const msaList = getMultiStationAdminStationList(userSession);
        if (msaList.includes(bidEvent.station) || userSession.activeStation === bidEvent.station) {
          setBidData(bidEvent);

          const bidGroupEventsResponse = await fetchLatestBidGroupEmpEventsByReferenceNumber(
            userSession.eid,
            bidReferenceNumber
          );
          const bidGroupEventsResult = await bidGroupEventsResponse.json();
          if (bidGroupEventsResult.events) {
            const eidFromPath = location.pathname.split("/")[3];
            const employeeInGroup = bidGroupEventsResult.events.find((e) => e.eid === eidFromPath && !e.isRemoved);
            if (employeeInGroup) {
              setIsPageLoading(false);
              const employeeBid = buildEmployeeDataFromBidRedirect(bidEvent, employeeInGroup);
              setEmployeeIdSearchTriggered(true);
              setEmployeeIdSearchLoaded(true);
              setEmployeeId(employeeBid.eid);
              setEmployeeData(employeeBid);
              setIsBaseScheduleFromBidRedirect(true);
              setIsDialogOpen(true);
            } else {
              throw new Error("Employee not found in bid group");
            }
          } else {
            throw new Error("Error in fetchLatestBidGroupEmpEventsByReferenceNumber: ", bidGroupEventsResult);
          }
        } else {
          throw new Error("User does not have access to this bid");
        }
      }
    } catch (error) {
      setIsPageLoading(false);
      setHasAccessToViewPage(false);
      console.error("Error in fetchLatestBidGroupEmpEventsByReferenceNumber: ", error);
    }
  };
  //#endregion

  //#region Input Utils
  const pathnameHasBidData = (urlPathname) => {
    const referenceNumberRegex = /\d{8}/.test(urlPathname);
    const eidRegex = /e\d+/.test(urlPathname);
    return referenceNumberRegex && eidRegex;
  };

  const clearForm = () => {
    setEmployeeIdErrors("");
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(false);
    setEmployeeData(null);
    setEmployeeSchedules([]);
  };
  //#endregion

  //#region Event Handlers
  const handleStationFilterChange = (event) => {
    clearForm();
    setEmployeeId("");
    setStation(event.target.value);
    dispatch(updateStationFilter(event.target.value));
  };

  const handleEmployeeIdChange = (eid) => {
    if (employeeData) {
      setEmployeeData(null);
    }
    if (employeeSchedules) {
      setEmployeeSchedules([]);
    }
    setEmployeeId(eid.target.value.toLowerCase().trim());
  };

  const handleEmployeeIdBlur = (event) => {
    let trimEmpId = event?.target?.value?.trim();
    if (trimEmpId !== employeeData?.eid) {
      clearForm();
    }
    let errors = validateScheduleEmployeeId(trimEmpId, userSession.eid);
    if (employeeIdErrors === "" || errors !== "") {
      setEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (!employeeData || trimEmpId !== employeeData.eid) {
        fetchEmployeeData(trimEmpId);
      }
    }
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const handleEmployeeDataClear = () => {
    setEmployeeId("");
    clearForm();
    let errors = validateScheduleEmployeeId("", userSession.eid);
    if (errors !== "") {
      setEmployeeIdErrors(errors);
    }
  };

  const handlePreviousMonthClick = () => {
    const currentYear = getCurrentYear(station);
    let selectedMonth = month;
    let selectedYear = year;
    if (year === currentYear) {
      const currentMonthIdx = selectableMonths.indexOf(month);
      if (currentMonthIdx === 0) {
        const previousYear = parseInt(year) - 1;
        const newMonths = getSelectableMonthsList(previousYear.toString(), station);
        setSelectableMonths(newMonths);
        selectedMonth = newMonths[newMonths.length - 1];
        selectedYear = previousYear.toString();
        setMonth(selectedMonth);
        setYear(selectedYear);
      } else {
        selectedMonth = selectableMonths[currentMonthIdx - 1];
        setMonth(selectedMonth);
      }
    } else if (year === (parseInt(currentYear) + 1).toString()) {
      const currentMonthIdx = selectableMonths.indexOf(month);
      if (currentMonthIdx === 0) {
        const newMonths = getSelectableMonthsList(currentYear, station);
        setSelectableMonths(newMonths);
        selectedMonth = newMonths[newMonths.length - 1];
        selectedYear = currentYear;
        setMonth(selectedMonth);
        setYear(selectedYear);
      } else {
        selectedMonth = selectableMonths[currentMonthIdx - 1];
        setMonth(selectedMonth);
      }
    } else {
      // year is previous available year
      const currentMonthIdx = selectableMonths.indexOf(month);
      selectedMonth = selectableMonths[currentMonthIdx - 1];
      setMonth(selectedMonth);
    }
  };

  const handleNextMonthClick = () => {
    const currentYear = getCurrentYear(station);
    let selectedMonth = month;
    let selectedYear = year;
    if (year === currentYear) {
      const currentMonthIdx = selectableMonths.indexOf(month);
      if (currentMonthIdx === selectableMonths.length - 1) {
        const nextYear = (parseInt(year) + 1).toString();
        const newMonths = getSelectableMonthsList(nextYear, station);
        setSelectableMonths(newMonths);
        selectedMonth = newMonths[0];
        selectedYear = nextYear;
        setMonth(selectedMonth);
        setYear(selectedYear);
      } else {
        selectedMonth = selectableMonths[currentMonthIdx + 1];
        setMonth(selectedMonth);
      }
    } else if (year === (parseInt(currentYear) - 1).toString()) {
      const currentMonthIdx = selectableMonths.indexOf(month);
      if (currentMonthIdx === selectableMonths.length - 1) {
        const newMonths = getSelectableMonthsList(currentYear, station);
        setSelectableMonths(newMonths);
        selectedMonth = newMonths[0];
        selectedYear = currentYear;
        setMonth(selectedMonth);
        setYear(selectedYear);
      } else {
        selectedMonth = selectableMonths[currentMonthIdx + 1];
        setMonth(selectedMonth);
      }
    } else {
      // year is next available year
      const currentMonthIdx = selectableMonths.indexOf(month);
      selectedMonth = selectableMonths[currentMonthIdx + 1];
      setMonth(selectedMonth);
    }
  };

  const handleMonthChange = (event) => {
    const selectedMonth = event.target.value;
    setMonth(selectedMonth);
  };

  const handleYearChange = (event) => {
    const selectedYear = event.target.value;
    let selectedMonth = month;
    setYear(selectedYear);
    const newSelectableMonths = getSelectableMonthsList(selectedYear, station);
    setSelectableMonths(newSelectableMonths);
    if (!newSelectableMonths.includes(month)) {
      if (selectedYear === getCurrentYear()) {
        selectedMonth = getCurrentMonth();
        setMonth(selectedMonth);
      } else {
        selectedMonth = newSelectableMonths[newSelectableMonths.length - 1];
        setMonth(selectedMonth);
      }
    }
  };

  const handleTodayClicked = () => {
    const currentYear = getCurrentYear(station);
    const currentMonth = getCurrentMonth(station);
    if (year !== currentYear || month !== currentMonth) {
      setYear(currentYear);
      setMonth(currentMonth);
      setSelectableMonths(getSelectableMonthsList(currentYear, station));
    }
  };

  const handleEmployeeDailyScheduleDialogOpen = (dayState) => {
    //TODO: Open dialog for dayState
  };
  //#endregion

  //#region Component Renders
  const CancelButtonEmployeeId = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleEmployeeDataClear}>
        <CancelIcon />
      </IconButton>
    );
  };

  const BackButtonMonthSelection = () => {
    return (
      <IconButton
        size="small"
        className="back-icon-button"
        onMouseDown={handlePreviousMonthClick}
        disabled={year === selectableYears[0] && month === selectableMonths[0]}
      >
        <ArrowBackIosNewIcon />
      </IconButton>
    );
  };

  const ForwardButtonMonthSelection = () => {
    return (
      <IconButton
        size="small"
        className="forward-icon-button"
        onMouseDown={handleNextMonthClick}
        disabled={
          year === selectableYears[selectableYears.length - 1] &&
          month === selectableMonths[selectableMonths.length - 1]
        }
      >
        <ArrowForwardIosIcon />
      </IconButton>
    );
  };

  const renderDesktopScheduleManagementPage = () => {
    return (
      <>
        <Grid>
          <StarsBreadcrumbs
            isConfirmationRequired={false}
            breadcrumbsLinks={breadcrumbsLink}
            breadcrumbsList={breadcrumbsList}
          />
        </Grid>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item container justifyContent="flex-start" md={8} lg={8}>
            <Grid container className={classes.pageTitleLeft}>
              Base Schedule Management
            </Grid>
            <br />
          </Grid>
        </Grid>
        {userSession.isAdmin && (
          <Grid
            container
            sx={{
              pt: 3,
              display: "flex",
              justifyContent: "start",
              paddingTop: "10px",
              paddingLeft: "3rem",
              paddingRight: "3rem",
            }}
            spacing={2}
          >
            <Grid item md={2}>
              <div className={classes.scheduleInputRow}>
                <div>
                  {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
                    <FormControl>
                      <StarsSelectInput
                        label={STATION}
                        menuMessage={STATION}
                        value={stationFilter}
                        allOptions={getMultiStationAdminStationList(userSession)}
                        sortComparator={sortOnCaseSensitiveTexts}
                        onChange={(event) => handleStationFilterChange(event)}
                        errors={stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
                        className={classes.inputSelect}
                        isMenuItemDisabled={(station) => !props?.featureConfig?.userStations?.includes(station)}
                      />
                    </FormControl>
                  ) : (
                    <TextField
                      className={classes.inputSelect}
                      label={STATION}
                      variant="outlined"
                      value={stationFilter}
                      disabled
                    />
                  )}
                </div>
                <div className={classes.scheduleInputEmployeeID}>
                  <TextField
                    className={classes.inputText}
                    label={EMPLOYEE_ID}
                    variant="outlined"
                    value={employeeId}
                    onChange={handleEmployeeIdChange}
                    onBlur={handleEmployeeIdBlur}
                    onKeyUp={handleEmployeeIdEnterKeyPressed}
                    InputProps={{
                      endAdornment:
                        employeeId !== "" ? (
                          <InputAdornment position="start">
                            <CancelButtonEmployeeId />
                          </InputAdornment>
                        ) : (
                          ""
                        ),
                    }}
                    error={employeeIdErrors !== ""}
                    helperText={employeeIdErrors}
                  />
                </div>
              </div>
            </Grid>
            <Grid item md={6}>
              {employeeIdSearchTriggered ? (
                employeeIdSearchLoaded ? (
                  employeeIdErrors === "" && employeeData ? (
                    <Grid container className={classes.employeeDetails}>
                      <Grid item xs={4} className={classes.employeeName}>
                        {employeeData.lastName + ", " + employeeData.firstName}
                      </Grid>
                      <Grid item xs={8} className={classes.employeeData}>
                        <Stack direction="row">
                          <Box ml={2} mr={2}>
                            <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                              HOME STATION
                            </Typography>
                            <p className={classes.employeeDataValue}>{employeeData.homeStation}</p>
                          </Box>
                          {employeeData.tempStation ? (
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                TEMP STATION
                              </Typography>
                              <p className={classes.employeeDataValue}>{employeeData.tempStation}</p>
                            </Box>
                          ) : (
                            ""
                          )}
                          <Box ml={2} mr={2}>
                            <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                              WORKGROUP
                            </Typography>
                            <p className={classes.employeeDataValue}>
                              {employeeData.workgroup ?? getWorkgroupGivenJobCode(employeeData.jobCode)}
                            </p>
                          </Box>
                          <Box ml={2} mr={2}>
                            <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                              STATUS
                            </Typography>
                            <p className={classes.employeeDataValue}>{employeeData.ftpt}</p>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  ) : (
                    ""
                  )
                ) : (
                  <CircularProgress sx={{ float: "left", marginTop: "7px", marginLeft: "70px" }} />
                )
              ) : (
                ""
              )}
            </Grid>
            <Grid item md={4}>
              <div className={classes.scheduleInputDate}>
                <FormControl>
                  <StarsSelectInput
                    label="Month"
                    value={month}
                    allOptions={selectableMonths}
                    onChange={handleMonthChange}
                    errors={""}
                    className={classes.scheduleInputMonth}
                  />
                </FormControl>
                <FormControl>
                  <StarsSelectInput
                    label="Year"
                    value={year}
                    allOptions={selectableYears}
                    onChange={handleYearChange}
                    errors={""}
                    className={classes.inputSelect}
                  />
                </FormControl>
              </div>
            </Grid>
            <Grid container sx={{ marginTop: "20px" }}>
              <Grid xs={12}>
                <Alert severity="warning">The purpose of this screen is to add/update Base Schedules in STARS so Employees can participate in Monthly Time Off Bids. Only key information for Monthly Time Off Bids (Base Schedules, Awarded Time Off Bids, Approved Ad-Hoc Time Off Requests) is displayed. Refer to current staffing tools for Employees' latest schedules.</Alert>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              sx={{
                backgroundColor: "secondary.swaLightestBlue",
                paddingTop: "10px",
                paddingBottom: "10px",
                // marginTop: "20px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Grid container md></Grid>
              <Grid container md={3}>
                <Grid item md={2}>
                  <BackButtonMonthSelection />
                </Grid>
                <Grid item md={8}>
                  <Typography className={classes.scheduleCalendarHeader} component="span">
                    {month + " " + year}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <ForwardButtonMonthSelection />
                </Grid>
              </Grid>
              <Grid container md sx={{ direction: "row", justifyContent: "flex-end", marginRight: "20px" }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    marginRight: "10px",
                  }}
                  onClick={() => {
                    setIsDialogOpen(true);
                  }}
                  disabled={!employeeData || employeeData.tempStation === station}
                >
                  Create Base Schedule
                </Button>
                <Button variant="outlined" onClick={handleTodayClicked}>
                  Today
                </Button>
              </Grid>
            </Grid>
            <Grid container>
              <ScheduleEditorCalendar
                dayState={getDayStateForScheduleEditor(year, month, employeeSchedules)}
                station={station}
                DayComponent={EmployeeDailySchedule}
                handleDialogOpen={handleEmployeeDailyScheduleDialogOpen}
                isLoading={isEmployeeScheduleLoading}
              />
            </Grid>
          </Grid>
        )}
        {isDialogOpen && (
          <CustomBaseScheduleDialog
            isDialogOpen={isDialogOpen}
            setIsDialogOpen={setIsDialogOpen}
            isWorkgroupInputDisabled={isBaseScheduleFromBidRedirect}
            station={isBaseScheduleFromBidRedirect ? bidData.station : station}
            workgroup={employeeData ? employeeData.workgroup ?? getWorkgroupGivenJobCode(employeeData.jobCode) : ""}
            employeeFirstName={employeeData ? employeeData.firstName : ""}
            employeeLastName={employeeData ? employeeData.lastName : ""}
            employeeId={employeeData ? employeeData.eid : ""}
          />
        )}
      </>
    );
  };

  const renderMobileScheduleManagementPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  } else if (!hasAccessToViewPage) {
    return <div className="page-error">{NO_ACCESS_TO_PAGE_MESSAGE}</div>;
  }
  //#endregion

  return (
    <div className="App-contents enable-scroll" data-testid="schedule-management-page">
      {isUserAuthorizedStateLoading || isPageLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopScheduleManagementPage() : renderMobileScheduleManagementPage()}</>
      )}
    </div>
  );
};

ScheduleManagementPage.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default ScheduleManagementPage;
